// src/ui/ToastItem.tsx
// Logical wrapper around <Toast /> that adds:
// - auto-close after N ms (with optional pause on hover)
// - bottom progress bar (width reflects remaining time)
// - exit animation (slide-right + fade) before unmounting
// Parent still controls list/queue; this component handles its own lifecycle.

import * as React from "react";
import clsx from "classnames";
import { Toast, ToastProps, ToastVariant } from "./Toast";

export interface ToastItemProps extends Omit<ToastProps, "exiting" | "bottomSlot" | "onClose"> {
  /** Auto-close after N milliseconds; 0/undefined disables */
  autoCloseMs?: number;
  /** Show progress bar when autoCloseMs is set (default: true) */
  showProgress?: boolean;
  /** Pause progress when hovered (default: true) */
  pauseOnHover?: boolean;
  /** Exit transition duration (ms) */
  exitDurationMs?: number;
  /** Called when the toast fully finishes (after exit animation) */
  onClose?: () => void;
  /** Called when user clicks × or when auto-close starts (before animation) */
  onCloseRequest?: () => void;
}

export const ToastItem: React.FC<ToastItemProps> = ({
  autoCloseMs,
  showProgress = true,
  pauseOnHover = true,
  exitDurationMs = 250,
  onClose,
  onCloseRequest,
  className,
  variant = "info",
  ...toastProps
}) => {
  const [hover, setHover] = React.useState(false);
  const [progress, setProgress] = React.useState(1); // 1 → full time remaining
  const [exiting, setExiting] = React.useState(false);
  const [closed, setClosed] = React.useState(false);

  const rafRef = React.useRef<number | null>(null);
  const startedAtRef = React.useRef<number | null>(null);
  const elapsedRef = React.useRef(0);
  const autoMs = autoCloseMs ?? 0;

  const cancelRAF = React.useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // reset progress when duration changes
  React.useEffect(() => {
    setProgress(1);
    elapsedRef.current = 0;
    startedAtRef.current = null;
    cancelRAF();
  }, [autoMs, cancelRAF]);

  // start exit (slide + fade)
  const startExit = React.useCallback(() => {
    if (exiting || closed) return;
    onCloseRequest?.();
    setExiting(true);
    cancelRAF();
  }, [exiting, closed, onCloseRequest, cancelRAF]);

  // drive progress + auto close
  React.useEffect(() => {
    if (!autoMs || autoMs <= 0 || exiting || closed) return;

    const step = (t: number) => {
      if (pauseOnHover && hover) {
        startedAtRef.current = null; // paused
      } else {
        if (startedAtRef.current == null) startedAtRef.current = t;
        const elapsed = elapsedRef.current + (t - startedAtRef.current);
        const remaining = Math.max(0, autoMs - elapsed);
        setProgress(remaining / autoMs);
        if (remaining === 0) {
          startExit();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return cancelRAF;
  }, [autoMs, hover, pauseOnHover, exiting, closed, startExit, cancelRAF]);

  // accumulate elapsed while hovering (pause)
  React.useEffect(() => {
    if (!autoMs || autoMs <= 0 || exiting || closed) return;
    if (pauseOnHover && hover && startedAtRef.current != null) {
      const now = performance.now();
      elapsedRef.current += now - (startedAtRef.current ?? now);
      startedAtRef.current = null;
    }
  }, [hover, pauseOnHover, autoMs, exiting, closed]);

  // unmount after exit transition ended
  const onTransitionEnd = React.useCallback(() => {
    if (exiting && !closed) {
      setClosed(true);
      onClose?.();
    }
  }, [exiting, closed, onClose]);

  if (closed) return null;

  // build bottom progress slot
  const ProgressBar =
    autoMs > 0 && showProgress ? (
      <div
        aria-hidden
        className={clsx(
          "tw-absolute tw-left-0 tw-right-0 tw-bottom-0",
          "tw-h-[3px] tw-rounded-b-2xl",
          // track color based on variant tokens (uses same mapping inside <Toast />)
          // we re-use classNames by reading variant in parent
          // track class is identical for all variants; tint is applied on inner bar
          "tw-bg-base-300"
        )}
      >
        <div
          className={clsx("tw-h-full", variantClass(variant).progress)}
          style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }}
        />
      </div>
    ) : null;

  return (
    <Toast
      {...toastProps}
      variant={variant}
      exiting={exiting}
      onMouseEnter={(e) => {
        toastProps.onMouseEnter?.(e);
        setHover(true);
      }}
      onMouseLeave={(e) => {
        toastProps.onMouseLeave?.(e);
        setHover(false);
      }}
      onClick={toastProps.onClick}
      onClose={startExit}
      onTransitionEnd={(e) => {
        toastProps.onTransitionEnd?.(e);
        // Ensure we listen on outer container only
        if (e.target === e.currentTarget) onTransitionEnd();
      }}
      className={clsx("tw-duration-200", className)}
      style={{ ...(toastProps.style || {}), transitionDuration: `${exitDurationMs}ms` }}
      bottomSlot={ProgressBar}
    />
  );
};

// helper to reuse variant progress class (kept in sync with Toast mapping)
function variantClass(variant: ToastVariant) {
  const map = {
    info: { progress: "tw-bg-neutral" },
    success: { progress: "tw-bg-neutral" },
    warning: { progress: "tw-bg-neutral" },
    error: { progress: "tw-bg-neutral" },
    neutral: { progress: "tw-bg-neutral" },
  } as const;
  return map[variant] ?? map.info;
}
